/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import { listEventTypes } from '@ba-ui-toolkit/constants';
import { forOwn } from './lodash';

const { selectionEventTypes } = listEventTypes;

/**
 * Get the array of selected data elements from the provided selection map.
 *
 * @param {Object} selectedMap a map of selected values
 *
 * @return {Object[]} an array of selected data elements
 */
export function getSelected(selectedMap) {
	const result = [];
	forOwn(selectedMap, (value, i) => {
		if (selectedMap[i] != null) {
			result.push(selectedMap[i]);
		}
	});

	return result;
}

/**
 * A key getter that simply returns the index provided.
 *
 * @param {number} index the index of the data element
 *
 * @return {number} the index of the data element
 */
export function indexKeyGetter(index) {
	return index;
}

/**
 * Handles events for single selection mode lists.
 *
 * @param {Object} event the list selection event
 * @param {Object[]} data the array of data elements
 * @param {string} selectedKey the current selected key value
 * @param {keyGetterCallback} keyGetter
 *
 * @return {string} the key of the selected element
 */
export function singleSelect(event, data, selectedKey, keyGetter) {
	const getter = keyGetter || indexKeyGetter;
	const key = getter(event.index);
	if (event.type === selectionEventTypes.TOGGLE && selectedKey === key) {
		return null;
	}

	return key;
}

/**
 * Clears all data elements from the given selection map.
 *
 * @param {Object[]} data the array of data elements to clear
 * @param {Object} selectedMap a map of selected values
 * @param {keyGetterCallback} keyGetter
 * @param {Object} omit a map of items to omit from changing selection
 *
 * @return {Object} the new selection map
 */
export function clear(data, selectedMap, keyGetter, omit) {
	const getter = keyGetter || indexKeyGetter;
	const newSelection = { ...selectedMap };
	data.forEach((d, i) => {
		const key = getter(i);
		if (selectedMap[key] && (!omit || !omit[key])) {
			newSelection[key] = null;
		}
	});

	return newSelection;
}

/**
 * Inverts the selection of all data elements from the given selection map.
 *
 * @param {Object[]} data the array of data elements to invert
 * @param {Object} selectedMap a map of selected values
 * @param {keyGetterCallback} keyGetter
 * @param {Object} omit a map of items to omit from changing selection
 *
 * @return {Object} the new selection map
 */
export function invert(data, selectedMap, keyGetter, omit) {
	const getter = keyGetter || indexKeyGetter;
	const newSelection = { ...selectedMap };
	data.forEach((d, i) => {
		const key = getter(i);
		if (!omit || !omit[key]) {
			if (!selectedMap[key]) {
				newSelection[key] = data[i];
			} else {
				newSelection[key] = null;
			}
		}
	});

	return newSelection;
}

/**
 * Handles events for multi selection mode lists.
 *
 * @param {Object} event the list selection event
 * @param {Object[]} data the array of data elements
 * @param {string} selectedKey the current selected key value
 * @param {keyGetterCallback} keyGetter
 * @param {Object} omit a map of items to omit from changing selection
 *
 * @return {string} the key of the selected element
 */
export function multiSelect(event, data, selectedMap, keyGetter, omit) {
	const eventType = event.type;
	const getter = keyGetter || indexKeyGetter;
	const result = {
		selectedCount: 0,
		deselectedCount: 0,
	};
	let rselectedMap = selectedMap;
	let key;
	const { index } = event;
	switch (eventType) {
		case selectionEventTypes.SET:
			key = getter(index);
			if (!omit) {
				const total = Object.keys(selectedMap).length;
				if (total !== 1 || !selectedMap[key]) {
					rselectedMap = {
						[key]: data[index],
					};
					result.selected = data[index];
					result.selectedCount = 1;
					result.deselectedCount = total;
				}
			} else if (!omit[key] && !selectedMap[key]) {
				rselectedMap = { ...selectedMap };
				result.selected = data[index];
				rselectedMap[key] = data[index];
				result.selectedCount = 1;
			}
			break;

		default:
			rselectedMap = { ...selectedMap };

			switch (eventType) {
				case selectionEventTypes.ADD:
					key = getter(index);
					if (!rselectedMap[key] && (!omit || !omit[key])) {
						rselectedMap[key] = data[index];
						result.selected = data[index];
						result.selectedCount = 1;
					}
					break;

				case selectionEventTypes.REMOVE:
					key = getter(index);
					if (rselectedMap[key] && (!omit || !omit[key])) {
						delete rselectedMap[key];
						result.deselected = data[index];
						result.deselectedCount = 1;
					}
					break;

				case selectionEventTypes.COMPOSITE:
					if (event.added) {
						event.added.forEach(i => {
							key = getter(i);
							if (!rselectedMap[key] && (!omit || !omit[key])) {
								rselectedMap[key] = data[i];
								result.selected = data[index];
								result.selectedCount += 1;
							}
						});
					}

					if (event.removed) {
						event.removed.forEach(i => {
							key = getter(i);
							if (rselectedMap[key] && (!omit || !omit[key])) {
								delete rselectedMap[key];
								result.deselected = data[index];
								result.deselectedCount += 1;
							}
						});
					}
					break;

				case selectionEventTypes.TOGGLE:
					key = getter(index);
					if (!omit || !omit[key]) {
						if (rselectedMap[key]) {
							delete rselectedMap[key];
							result.deselected = data[index];
							result.deselectedCount = 1;
						} else {
							rselectedMap[key] = data[index];
							result.selected = data[index];
							result.selectedCount = 1;
						}
					}
					break;

				case selectionEventTypes.SELECT_ALL:
					data.forEach((d, i) => {
						key = getter(i);
						if (!rselectedMap[key] && (!omit || !omit[key])) {
							rselectedMap[key] = data[i];
							result.selected = data[index];
							result.selectedCount += 1;
						}
					});
					break;
				default:
			}
	}

	result.selectedMap = rselectedMap;

	return result;
}
